import { useState, useEffect } from 'react';
import { fg } from '@atlaskit/platform-feature-flags';
import {
	getSearchConfigV2,
	type DegradationCallback,
	type TSearchConfigAPIResponse,
} from '@atlassian/search-client';
export type { TProductEdition, TProductDataRegions } from '@atlassian/search-client';
import { useAnalytics } from '../analytics';
import { onDegradedDependency, onFailedDependency } from '../analytics/events';

const getProductEdition = (firstPartyResponse?: TSearchConfigAPIResponse, cloudId?: string) => {
	if (!cloudId || !firstPartyResponse || !firstPartyResponse.siteMetadata) {
		return undefined;
	}

	return firstPartyResponse.siteMetadata.find(
		(metadata) => metadata.siteId === cloudId && !!metadata.productEdition,
	)?.productEdition;
};

const getProductDataRegions = (firstPartyResponse?: TSearchConfigAPIResponse, cloudId?: string) => {
	if (!cloudId || !firstPartyResponse || !firstPartyResponse.siteMetadata) {
		return undefined;
	}

	return firstPartyResponse.siteMetadata.find(
		(metadata) => metadata.siteId === cloudId && !!metadata.productDataRegions,
	)?.productDataRegions;
};

const getJiraIssueIndexedForTenant = (
	firstPartyResponse?: TSearchConfigAPIResponse,
	cloudId?: string,
) => {
	if (!cloudId || !firstPartyResponse || !firstPartyResponse.siteMetadata) {
		return false;
	}
	return firstPartyResponse.siteMetadata.find((metadata) => metadata.siteId === cloudId)
		?.isJiraIssueIndexed;
};

export type UseFetchConfigProps = {
	userId?: string | null;
	cloudId?: string;
	/**
	 * When passed this would be used as is to invoke the endpoint which returns search-configuration i.e. list of products accessible by the logged in user.
	 * This is useful for products like Trello where the API gateway is not on the host domain, but a completely different URL.
	 *
	 * No need to pass if the UI is being bootstrapped inside Jira, Confluence or any product tied to these sites.
	 */
	configFetchAbsoluteUrl?: string;
	aggregatorHostName?: string;
};

export const useFetchConfig = ({
	cloudId,
	configFetchAbsoluteUrl,
	aggregatorHostName,
	userId,
}: UseFetchConfigProps) => {
	const [firstPartyResponse, setFirstPartyResponse] = useState<TSearchConfigAPIResponse>();

	const { fireAnalyticsEvent } = useAnalytics();

	useEffect(() => {
		if (!firstPartyResponse && fg('jira_enable_qs_graphql')) {
			// DegradedDependency may not be visible to the end-user due to request caching. This is a health metric for our BE
			const degradationCallback: DegradationCallback = (reason) => {
				fireAnalyticsEvent(
					onDegradedDependency({
						reason,
					}),
				);
			};

			/**
			 * These parameters define a caching strategy where:
			 *  - Cache is refreshed only if the cached data is expired, with a 1 minute TTL
			 *  - Cached value is returned, regardless of whether its expired or not
			 */
			const searchConfigParams = {
				cloudId,
				absoluteUrl: configFetchAbsoluteUrl,
				hostName: aggregatorHostName,
				userId,
				degradationCallback,
				expiration: 1000 * 60, // 1 minute
				returnExpired: true,
				onlyRefreshIfExpired: true,
			};

			getSearchConfigV2(searchConfigParams)
				.then((response) => {
					setFirstPartyResponse(response);
				})
				.catch(() => {
					// FailedDependency is visible to the end-user. This is a health metric for our UX
					fireAnalyticsEvent(onFailedDependency());

					setFirstPartyResponse({
						resources: [],
						intercomHmac: '',
						siteMetadata: [],
					});
				});
		}
	}, [
		firstPartyResponse,
		setFirstPartyResponse,
		cloudId,
		configFetchAbsoluteUrl,
		aggregatorHostName,
		userId,
		fireAnalyticsEvent,
	]);

	return {
		productEdition: getProductEdition(firstPartyResponse, cloudId),
		productDataRegions: getProductDataRegions(firstPartyResponse, cloudId),
		isJiraIssueIndexed: getJiraIssueIndexedForTenant(firstPartyResponse, cloudId),
	};
};
